import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone, DropEvent } from 'react-dropzone';
import {
  cancelDocumentUpload,
  uploadDocument,
} from '../../redux/uploads/actions';
import { LegalDocumentType } from '../../redux/uploads/types';
import { getDocumentUploadByType } from '../../redux/uploads/selectors';

export interface DocumentUploadOptions {
  deviationId: number;
  documentType: LegalDocumentType;
  onUploadComplete: (documentUrl: string) => void;
}

/**
 *  Legal (narf/sprf) document uploader.
 */
export function useDocumentUpload({
  deviationId,
  documentType,
  onUploadComplete,
}: DocumentUploadOptions) {
  const dispatch = useDispatch();
  const activeUpload = useSelector(state =>
    getDocumentUploadByType(state, documentType)
  );

  const onFilePicked = useCallback(
    (files: File[], ev: DropEvent) => {
      if (!files || files.length < 1) {
        return;
      }

      dispatch(
        uploadDocument({
          file: files[0],
          documentType,
          deviationId,
          onSuccess: onUploadComplete,
        })
      );
    },
    [dispatch, deviationId, documentType, onUploadComplete]
  );

  const onCancel = useCallback(() => {
    dispatch(cancelDocumentUpload(documentType));
  }, [dispatch, documentType]);

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted: onFilePicked,
    multiple: false,
    noDrag: true,
  });

  const input = <input {...getInputProps()} />;

  const isLoading = Boolean(
    activeUpload && !activeUpload.error && !activeUpload.documentUrl
  );
  const errorMessage = (activeUpload?.error?.cause ||
    activeUpload?.error?.message) as string | undefined;

  return {
    input,
    activeUpload,
    isLoading,
    errorMessage,
    getRootProps,
    onCancel,
  };
}
